import { z } from 'zod';

export const playerSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  nickname: z.string().nullable(),
  photoUrl: z.string().nullable(),
  telegramUserId: z.number().nullable(),
  termsAccepted: z.boolean(),
  onboardingCompleted: z.boolean(),
  isNew: z.boolean(),
});
export type Player = z.infer<typeof playerSchema>;

export const otpRequestSchema = z.object({ devCode: z.string().nullable() });

export const notificationPrefsSchema = z.object({
  push: z.boolean(),
  tournaments: z.boolean(),
  rating: z.boolean(),
});
export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>;

export const authResponseSchema = z.object({
  token: z.string(),
  player: playerSchema,
});

export const seasonSchema = z.object({
  id: z.number(),
  name: z.string(),
  /** Every 10 seasons open a new chapter (absolute season 11 = chapter 2, season 1). */
  chapter: z.number(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  finaleAt: z.string().nullable(),
  isActive: z.boolean(),
  tournaments: z.number(),
  players: z.number(),
});
export type Season = z.infer<typeof seasonSchema>;

/* ── Зал славы: чемпионы сезонов ── */

export const championBriefSchema = z.object({
  playerId: z.number(),
  name: z.string(),
  photoUrl: z.string().nullable(),
  points: z.number(),
  wins: z.number(),
  tournaments: z.number(),
});
export type ChampionBrief = z.infer<typeof championBriefSchema>;

export const hallSeasonSchema = z.object({
  seasonId: z.number(),
  chapter: z.number(),
  status: z.enum(['finished', 'active']),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  finaleAt: z.string().nullable(),
  /** null: сезон активен или чемпион ещё не назначен. */
  champion: championBriefSchema.nullable(),
});
export type HallSeason = z.infer<typeof hallSeasonSchema>;

export const championTop3RowSchema = z.object({
  rank: z.number(),
  playerId: z.number(),
  name: z.string(),
  points: z.number(),
});

export const championCardSchema = z.object({
  seasonId: z.number(),
  chapter: z.number(),
  status: z.literal('finished'),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  champion: championBriefSchema.extend({
    title: z.string().nullable(),
    quote: z.string().nullable(),
    reward: z.string().nullable(),
  }),
  top3: z.array(championTop3RowSchema),
});
export type ChampionCard = z.infer<typeof championCardSchema>;

export const leaderboardRowSchema = z.object({
  rank: z.number(),
  playerId: z.number(),
  name: z.string(),
  points: z.number(),
  wins: z.number(),
  gamesPlayed: z.number(),
  isMe: z.boolean(),
});
export type LeaderboardRow = z.infer<typeof leaderboardRowSchema>;

export const leaderboardSchema = z.object({
  seasonId: z.number().nullable(),
  rows: z.array(leaderboardRowSchema),
  me: leaderboardRowSchema.nullable(),
});
export type Leaderboard = z.infer<typeof leaderboardSchema>;

export const recentTournamentSchema = z.object({
  gameId: z.number(),
  startTime: z.string(),
  title: z.string().nullable(),
  format: z.string().nullable(),
  position: z.number().nullable(),
  points: z.number(),
});

export const myTournamentSchema = recentTournamentSchema.extend({
  fieldSize: z.number(),
  seasonId: z.number().nullable(),
});
export type MyTournament = z.infer<typeof myTournamentSchema>;

export const tournamentResultSchema = z.object({
  game: z.object({
    id: z.number(),
    startTime: z.string(),
    title: z.string().nullable(),
    format: z.string().nullable(),
    seasonId: z.number().nullable(),
    fieldSize: z.number(),
  }),
  rows: z.array(
    z.object({
      position: z.number().nullable(),
      playerId: z.number(),
      name: z.string(),
      points: z.number(),
      isMe: z.boolean(),
    }),
  ),
});
export type TournamentResult = z.infer<typeof tournamentResultSchema>;

export const profileSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  nickname: z.string().nullable(),
  email: z.string().nullable(),
  photoUrl: z.string().nullable(),
  termsAccepted: z.boolean(),
  onboardingCompleted: z.boolean(),
  notifications: notificationPrefsSchema,
  seasonId: z.number().nullable(),
  stats: z.object({
    points: z.number(),
    rank: z.number().nullable(),
    wins: z.number(),
    gamesPlayed: z.number(),
    itmPercent: z.number(),
  }),
  recent: z.array(recentTournamentSchema),
});
export type Profile = z.infer<typeof profileSchema>;

export const publicProfileSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  photoUrl: z.string().nullable(),
  seasonId: z.number().nullable(),
  stats: z.object({
    points: z.number(),
    rank: z.number().nullable(),
    wins: z.number(),
    gamesPlayed: z.number(),
    itmPercent: z.number(),
  }),
  recent: z.array(recentTournamentSchema),
});
export type PublicProfile = z.infer<typeof publicProfileSchema>;

export const livePlayerSchema = z.object({
  playerId: z.number().nullable(),
  name: z.string(),
  photoUrl: z.string().nullable(),
  status: z.string(),
  stack: z.number(),
  tableNo: z.number().nullable(),
  rebuys: z.number(),
  finalPosition: z.number().nullable(),
  knockouts: z.number(),
  /** Provisional rating points earned so far (place points + bounty). */
  points: z.number(),
  isMe: z.boolean(),
});
export type LivePlayer = z.infer<typeof livePlayerSchema>;

export const liveSchema = z.object({
  live: z.boolean(),
  status: z.string(),
  updatedAt: z.string().nullable(),
  level: z.number(),
  smallBlind: z.number(),
  bigBlind: z.number(),
  ante: z.number(),
  isBreak: z.boolean(),
  isPaused: z.boolean(),
  levelEndsAt: z.string().nullable(),
  timeLeftSec: z.number(),
  /** Late registration: open until the END of the flagged blind level in Korin. */
  lateRegOpen: z.boolean(),
  lateRegTimeLeftSec: z.number(),
  lateRegEndsAt: z.string().nullable(),
  lateRegLevel: z.number().nullable(),
  /** Arrived players (IN_GAME + OUT), like Korin's display. */
  playersTotal: z.number(),
  playersIn: z.number(),
  totalRebuys: z.number(),
  knockouts: z.number(),
  startingStack: z.number(),
  avgStack: z.number(),
  chipsInPlay: z.number(),
  breakInSec: z.number(),
  breakEndsAt: z.string().nullable(),
  nextSmallBlind: z.number(),
  nextBigBlind: z.number(),
  /** Сетка призовых мест (место → провизорные очки); default([]) — совместимость
   *  со старыми кэшами/пейлоадами без поля. */
  prizes: z.array(z.object({ place: z.number(), points: z.number() })).default([]),
  players: z.array(livePlayerSchema),
});
export type Live = z.infer<typeof liveSchema>;

export const appStatusSchema = z.object({
  maintenance: z.boolean(),
  message: z.string().nullable(),
});
export type AppStatus = z.infer<typeof appStatusSchema>;

export const avatarUploadSchema = z.object({ photoUrl: z.string() });

export const tournamentSchema = z.object({
  id: z.number(),
  startTime: z.string(),
  dayOfWeek: z.string().nullable(),
  title: z.string().nullable(),
  format: z.string().nullable(),
  buyIn: z.number(),
  guarantee: z.number().nullable(),
  startingStack: z.number().nullable(),
  blindMinutes: z.number().nullable(),
  description: z.string().nullable(),
  status: z.string().nullable(),
  seasonId: z.number().nullable(),
  capacity: z.number().nullable(),
  registrationOpen: z.boolean(),
  registeredCount: z.number(),
  registeredByMe: z.boolean(),
  waitlistCount: z.number(),
  myWaitlisted: z.boolean(),
  myWaitlistPosition: z.number().nullable(),
  /** Past-list extras (T1 cards); null for upcoming tournaments. */
  playersCount: z.number().nullable(),
  winner: z.string().nullable(),
  myPlace: z.number().nullable(),
  myPoints: z.number().nullable(),
});
export type Tournament = z.infer<typeof tournamentSchema>;

export const registrationSchema = z.object({
  playerId: z.number(),
  nickname: z.string(),
  photoUrl: z.string().nullable(),
  registeredAt: z.string(),
  rank: z.number().nullable(),
  waitlisted: z.boolean(),
});
export type Registration = z.infer<typeof registrationSchema>;

export const registrationsSchema = z.object({
  players: z.array(registrationSchema),
  total: z.number(),
});
export type Registrations = z.infer<typeof registrationsSchema>;

export const newsSchema = z.object({
  id: z.number(),
  title: z.string(),
  body: z.string().nullable(),
  icon: z.string().nullable(),
  imageUrl: z.string().nullable(),
  isPinned: z.boolean(),
  publishedAt: z.string(),
});
export type NewsItem = z.infer<typeof newsSchema>;

export const homeSchema = z.object({
  nextTournament: tournamentSchema.nullable(),
  news: z.array(newsSchema),
});
export type Home = z.infer<typeof homeSchema>;

/* ── Достижения (design_handoff_achievements) ── */

export const seasonalStateSchema = z.enum(['active', 'fixed', 'missed']);
export type SeasonalState = z.infer<typeof seasonalStateSchema>;

export const achievementRowSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  secret: z.boolean(),
  masked: z.boolean(),
  kind: z.enum(['permanent', 'seasonal']),
  seasonId: z.number().nullable(),
  state: seasonalStateSchema.nullable(),
  level: z.number(),
  maxLevel: z.number(),
  progress: z.number().nullable(),
  nextThreshold: z.number().nullable(),
  higherIsBetter: z.boolean(),
  paused: z.boolean(),
  earnedPoints: z.number(),
  totalPoints: z.number(),
  unlockedAt: z.string().nullable(),
});
export type AchievementRow = z.infer<typeof achievementRowSchema>;

export const ladderStepSchema = z.object({
  n: z.number(),
  title: z.string().nullable(),
  threshold: z.number(),
  points: z.number(),
  unlockedAt: z.string().nullable(),
});

export const achievementDetailSchema = achievementRowSchema.extend({
  ladder: z.array(ladderStepSchema),
  daysLeft: z.number().nullable(),
  bySeasons: z.array(
    z.object({ seasonId: z.number(), state: seasonalStateSchema.nullable(), level: z.number() }),
  ),
  pinned: z.boolean(),
});
export type AchievementDetail = z.infer<typeof achievementDetailSchema>;

export const showcaseItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  icon: z.string(),
  level: z.number(),
  maxLevel: z.number(),
  kind: z.enum(['permanent', 'seasonal']),
  state: seasonalStateSchema.nullable(),
});
export type ShowcaseItem = z.infer<typeof showcaseItemSchema>;

export const achievementsSummarySchema = z.object({
  isOwner: z.boolean(),
  points: z.number(),
  loyalty: z
    .object({
      name: z.string(),
      discountPct: z.number(),
      icon: z.string().nullable(),
      next: z.object({ name: z.string(), threshold: z.number() }).nullable(),
    })
    .nullable(),
  doneCount: z.number(),
  totalCount: z.number(),
  recent: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      icon: z.string().nullable(),
      masked: z.boolean(),
      level: z.number(),
      at: z.string(),
    }),
  ),
  categories: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      done: z.number(),
      total: z.number(),
      seasonal: z.boolean(),
    }),
  ),
  seasonal: z
    .object({
      seasonId: z.number(),
      daysLeft: z.number().nullable(),
      done: z.number(),
      total: z.number(),
    })
    .nullable(),
  showcase: z.array(showcaseItemSchema),
});
export type AchievementsSummary = z.infer<typeof achievementsSummarySchema>;

export const achievementCategorySchema = z.object({
  id: z.union([z.number(), z.literal('seasonal')]),
  name: z.string(),
  rows: z.array(achievementRowSchema),
});
export type AchievementCategory = z.infer<typeof achievementCategorySchema>;

export const celebrationSchema = z.object({
  achievementId: z.number(),
  name: z.string(),
  icon: z.string(),
  level: z.number(),
  levelTitle: z.string().nullable(),
  points: z.number(),
  maxLevel: z.number(),
});
export type Celebration = z.infer<typeof celebrationSchema>;
